// Permission Management: imports and dependencies
import AdminActionIcon from "./AdminActionIcon";
import React, { useEffect, useMemo, useState } from "react";
import { API_BASE_URL } from "../config";
import { useToastMessageState } from "../components/Toast/Toast";
import "./PermissionManagement.css";
import "./PermissionManagement.mobile.css";
import "./PermissionManagement.employee-access.css";

// Data types and contracts
type Role = {
  id: number;
  roleName: string;
  description?: string;
  isActive: boolean;
};
type Cell = { actionId: number; actionName: string; permissionId?: number };
type Page = { id: number; name: string; permissions: Cell[] };
type Module = { id: number; name: string; pages: Page[] };
type Employee = {
  id: number;
  name: string;
  email: string;
  roleId: number;
  roleName?: string;
  isActive: boolean;
};
type Override = { permissionId: number; isAllowed: boolean | null };

// Constants and helper functions
const auth = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "application/json",
});
async function api(path: string, options: RequestInit = {}) {
  const r = await fetch(`${API_BASE_URL}/api/permissions/${path}`, {
    ...options,
    headers: { ...auth(), ...(options.headers || {}) },
    cache: "no-store",
  });
  const text = await r.text();
  let data: any = null;
  if (text)
    try {
      data = JSON.parse(text);
    } catch {
      throw Error(
        `Permission API returned HTML/text instead of JSON (${r.status}). Restart the updated API at ${API_BASE_URL}.`,
      );
    }
  if (r.status === 401) throw Error("Session expired. Please log in again.");
  if (r.status === 403)
    throw Error(data?.message || "Only Super Admin can manage permissions.");
  if (!r.ok)
    throw Error(data?.message || `Permission API failed (${r.status}).`);
  return data;
}

// Main component and state
export default function PermissionManagement({
  selectedSchoolId,
}: {
  selectedSchoolId?: number | null;
}) {
  const [tab, setTab] = useState("matrix"),
    [roles, setRoles] = useState<Role[]>([]),
    [catalog, setCatalog] = useState<Module[]>([]),
    [selected, setSelected] = useState<number>(),
    [grants, setGrants] = useState<Set<number>>(new Set()),
    [search, setSearch] = useState(""),
    [expanded, setExpanded] = useState<Set<number>>(new Set()),
    [stats, setStats] = useState<any>(),
    [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState<Employee[]>([]),
    [employeeId, setEmployeeId] = useState<number>(),
    [primaryRoleId, setPrimaryRoleId] = useState<number>(),
    [additionalRoles, setAdditionalRoles] = useState<Set<number>>(new Set()),
    [overrides, setOverrides] = useState<Map<number, boolean>>(new Map()),
    [employeeSearch, setEmployeeSearch] = useState(""),
    [showCustomAccess, setShowCustomAccess] = useState(false);
  const [employeePage, setEmployeePage] = useState(1);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [message, setMessage] = useToastMessageState();

  // Constants and helper functions
  const employeeQuery = selectedSchoolId ? `?schoolId=${selectedSchoolId}` : "";
  const load = async () => {
    setLoading(true);
    try {
      const [r, c, d] = await Promise.all(
        ["roles", "catalog", "dashboard"].map((path) => api(path)),
      );
      setRoles(r);
      setCatalog(c);
      setStats(d);
      setEmployees([]);
      setEmployeePage(1);
      setEmployeeId(undefined);
      setExpanded(new Set(c.map((x: Module) => x.id)));
      setSelected((v) => v || r[0]?.id);
      setMessage("");
    } catch (e: any) {
      setMessage(e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, [selectedSchoolId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (selected)
      api(`roles/${selected}`)
        .then((x) => setGrants(new Set(x)))
        .catch((e) => setMessage(e.message));
  }, [selected]);
  useEffect(() => {
    if (tab !== "employees") return;
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setEmployeesLoading(true);
      const params = new URLSearchParams({ page: String(employeePage), pageSize: "50" });
      if (selectedSchoolId) params.set("schoolId", String(selectedSchoolId));
      if (employeeSearch.trim()) params.set("search", employeeSearch.trim());
      try {
        const result = await api(`employees?${params}`, { signal: controller.signal });
        if (!controller.signal.aborted) setEmployees(result);
      } catch (error) {
        if (!controller.signal.aborted) setMessage(error instanceof Error ? error.message : "Unable to load employees.");
      } finally {
        if (!controller.signal.aborted) setEmployeesLoading(false);
      }
    }, 250);
    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [tab, employeeSearch, selectedSchoolId, employeePage]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (employeeId)
      api(`employees/${employeeId}${employeeQuery}`)
        .then((x) => {
          setPrimaryRoleId(x.employee.roleId);
          setAdditionalRoles(new Set(x.additionalRoleIds));
          setOverrides(
            new Map(
              x.overrides
                .filter((o: Override) => o.isAllowed !== null)
                .map((o: Override) => [o.permissionId, !!o.isAllowed]),
            ),
          );
          setShowCustomAccess(
            x.overrides.some((o: Override) => o.isAllowed !== null),
          );
        })
        .catch((e) => setMessage(e.message));
  }, [employeeId, selectedSchoolId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const mainRole = document.querySelector<HTMLSelectElement>(
      ".permission-card label>select",
    );
    if (mainRole) {
      mainRole.disabled = true;
      mainRole.title =
        "Main role employee profile se aata hai. Use Staff Edit page se change karein.";
    }
  }, [employeeId, primaryRoleId]);
  useEffect(() => {
    setOverrides(
      (current) => new Map([...current].filter(([, allowed]) => allowed)),
    );
    document
      .querySelectorAll<HTMLOptionElement>('option[value="deny"]')
      .forEach((option) => option.remove());
  }, [showCustomAccess, employeeId, catalog]);
  const actions = useMemo(
    () =>
      catalog.flatMap((m) => m.pages).find((p) => p.permissions.length)
        ?.permissions || [],
    [catalog],
  );
  const shown = useMemo(
    () =>
      catalog
        .map((m) => ({
          ...m,
          pages: m.pages.filter((p) =>
            (m.name + " " + p.name)
              .toLowerCase()
              .includes(search.toLowerCase()),
          ),
        }))
        .filter((m) => m.pages.length),
    [catalog, search],
  );
  const shownEmployees = useMemo(() => {
    const q = employeeSearch.trim().toLowerCase();
    return employees.filter(
      (e) =>
        !q ||
        `${e.name} ${e.email} ${e.roleName || ""}`.toLowerCase().includes(q),
    );
  }, [employees, employeeSearch]);
  const toggle = (id?: number) =>
    id &&
    setGrants((g) => {
      const n = new Set(g);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  const moduleToggle = (m: Module, on: boolean) =>
    setGrants((g) => {
      const n = new Set(g);
      m.pages
        .flatMap((p) => p.permissions)
        .forEach(
          (c) =>
            c.permissionId &&
            (on ? n.add(c.permissionId) : n.delete(c.permissionId)),
        );
      return n;
    });
  const allIds = catalog.flatMap((m) =>
    m.pages.flatMap((p) =>
      p.permissions.flatMap((c) => (c.permissionId ? [c.permissionId] : [])),
    ),
  );
  const save = async () => {
    if (!selected) return;
    try {
      await api(`roles/${selected}`, {
        method: "PUT",
        body: JSON.stringify({ permissionIds: [...grants] }),
      });
      setMessage("Permissions saved. Changes take effect immediately.");
      await load();
    } catch (e: any) {
      setMessage(e.message);
    }
  };
  const saveEmployee = async () => {
    if (!employeeId || !primaryRoleId) return;
    try {
      await api(`employees/${employeeId}${employeeQuery}`, {
        method: "PUT",
        body: JSON.stringify({
          primaryRoleId,
          additionalRoleIds: [...additionalRoles],
          overrides: [...overrides].map(([permissionId, isAllowed]) => ({
            permissionId,
            isAllowed,
          })),
        }),
      });
      setMessage("Employee access saved. It applies on the next request.");
    } catch (e: any) {
      setMessage(e.message);
    }
  };
  return (
    <div className="permission-page">
      <div className="permission-heading">
        <div>
          <h2>Role & Permission Management</h2>
          <p>Control menus, pages and actions without code changes.</p>
        </div>
        {tab === "matrix" && (
          <button
            className="btn btn-primary"
            disabled={!selected || loading}
            onClick={save}
          >
            <AdminActionIcon action="save" />
            Save Changes
          </button>
        )}
      </div>
      {message && <div className="permission-message">{message}</div>}
      <div className="permission-stats">
        {[
          ["Total Roles", stats?.totalRoles],
          ["Total Employees", stats?.totalEmployees],
          ["Active Permissions", stats?.activePermissions],
        ].map(([a, b]) => (
          <div className="permission-stat" key={String(a)}>
            <span>{a}</span>
            <strong>{b ?? 0}</strong>
          </div>
        ))}
      </div>
      <div className="permission-tabs">
        {[
          ["matrix", "Permission Matrix"],
          ["roles", "Roles"],
          ["employees", "Employee Access"],
        ].map((x) => (
          <button
            key={x[0]}
            className={tab === x[0] ? "active" : ""}
            onClick={() => setTab(x[0])}
          >
            {x[1]}
          </button>
        ))}
      </div>
      {loading && (
        <div className="permission-empty">Loading permission catalog…</div>
      )}
      {!loading && tab === "matrix" && (
        <>
          <div className="permission-toolbar">
            <select
              value={selected || ""}
              onChange={(e) => setSelected(+e.target.value)}
            >
              <option value="" disabled>
                Select role
              </option>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.roleName}
                </option>
              ))}
            </select>
            <input
              placeholder="Search modules or pages"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button onClick={() => setGrants(new Set(allIds))}>
              <AdminActionIcon action="select" />
              Select All
            </button>
            <button onClick={() => setGrants(new Set())}>
              <AdminActionIcon action="reset" />
              Clear All
            </button>
          </div>
          {!catalog.length ? (
            <div className="permission-empty">
              No permission catalog was returned.
            </div>
          ) : (
            <div className="permission-table-wrap">
              <table className="permission-table">
                <thead>
                  <tr>
                    <th>Module / Page</th>
                    {actions.map((a) => (
                      <th key={a.actionId}>{a.actionName}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {shown.map((m) => (
                    <React.Fragment key={m.id}>
                      <tr className="module-row">
                        <td>
                          <button
                            onClick={() =>
                              setExpanded((s) => {
                                const n = new Set(s);
                                n.has(m.id) ? n.delete(m.id) : n.add(m.id);
                                return n;
                              })
                            }
                          >
                            {expanded.has(m.id) ? "▾" : "▸"} {m.name}
                          </button>
                        </td>
                        <td colSpan={actions.length}>
                          <button onClick={() => moduleToggle(m, true)}>
                            Grant module
                          </button>{" "}
                          <button onClick={() => moduleToggle(m, false)}>
                            <AdminActionIcon action="reset" />
                            Clear
                          </button>
                        </td>
                      </tr>
                      {expanded.has(m.id) &&
                        m.pages.map((p) => (
                          <tr key={p.id}>
                            <td className="page-name">{p.name}</td>
                            {p.permissions.map((c) => (
                              <td key={c.actionId}>
                                <input
                                  aria-label={`${p.name} ${c.actionName}`}
                                  type="checkbox"
                                  checked={
                                    !!c.permissionId &&
                                    grants.has(c.permissionId)
                                  }
                                  onChange={() => toggle(c.permissionId)}
                                />
                              </td>
                            ))}
                          </tr>
                        ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      {!loading && tab === "roles" && (
        <div className="permission-card">
          <h3>Existing Roles</h3>
          {roles.map((r) => (
            <div key={r.id} className="role-line">
              <div>
                <strong>{r.roleName}</strong>
                <small>{r.description || "No description"}</small>
              </div>
              <span
                className={r.isActive ? "status-active" : "status-inactive"}
              >
                {r.isActive ? "Active" : "Inactive"}
              </span>
            </div>
          ))}
        </div>
      )}
      {!loading && tab === "employees" && (
        <div className="permission-grid">
          <div className="permission-card">
            <h3>1. Select Employee</h3>
            <small>Choose the person whose access you want to change.</small>
            <input
              placeholder="Search by name, email or role"
              value={employeeSearch}
              onChange={(e) => { setEmployeeSearch(e.target.value); setEmployeePage(1); }}
            />
            {shownEmployees.map((e) => (
              <button
                key={e.id}
                className={`employee-pick ${employeeId === e.id ? "active" : ""}`}
                onClick={() => setEmployeeId(e.id)}
              >
                <strong>{e.name}</strong>
                <small>
                  {e.email} · Current role: {e.roleName || "Not assigned"}
                </small>
              </button>
            ))}
            {(employeePage > 1 || employees.length === 50) && (
              <nav aria-label="Employee pages">
                <button disabled={employeesLoading || employeePage === 1} onClick={() => setEmployeePage((page) => page - 1)}>Previous</button>
                <span> Page {employeePage} </span>
                <button disabled={employeesLoading || employees.length < 50} onClick={() => setEmployeePage((page) => page + 1)}>Next</button>
              </nav>
            )}
            {!shownEmployees.length && (
              <p>
                <small>No employee found.</small>
              </p>
            )}
          </div>
          {!employeeId ? (
            <div className="permission-empty">
              <h3>Select an employee first</h3>
              <p>Their role and access settings will appear here.</p>
            </div>
          ) : (
            <div className="permission-card">
              <h3>2. Choose Main Role</h3>
              <p>
                <small>
                  The employee normally gets all access included in this role.
                </small>
              </p>
              <label>
                Main role
                <select
                  value={primaryRoleId || ""}
                  onChange={(e) => setPrimaryRoleId(+e.target.value)}
                >
                  {roles
                    .filter((r) => r.id !== 1 && r.isActive)
                    .map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.roleName}
                      </option>
                    ))}
                </select>
              </label>
              <h3>
                3. Extra Roles <small>(optional)</small>
              </h3>
              <p>
                <small>
                  Only select these if the employee also does another job. Most
                  employees do not need this.
                </small>
              </p>
              {roles
                .filter(
                  (r) => r.id !== 1 && r.id !== primaryRoleId && r.isActive,
                )
                .map((r) => (
                  <label className="feature-line" key={r.id}>
                    <span>{r.roleName}</span>
                    <input
                      type="checkbox"
                      checked={additionalRoles.has(r.id)}
                      onChange={(e) =>
                        setAdditionalRoles((s) => {
                          const n = new Set(s);
                          e.target.checked ? n.add(r.id) : n.delete(r.id);
                          return n;
                        })
                      }
                    />
                  </label>
                ))}
              <button
                type="button"
                className="btn"
                onClick={() => setShowCustomAccess((v) => !v)}
              >
                {showCustomAccess
                  ? "Hide special access"
                  : "Set special access (optional)"}
              </button>
              {showCustomAccess && (
                <>
                  <h3>Special Access</h3>
                  <p>
                    <small>
                      Use this only when this employee needs an exception. “Use
                      role setting” is recommended.
                    </small>
                  </p>
                  <div className="permission-table-wrap">
                    <table className="permission-table">
                      <thead>
                        <tr>
                          <th>Page</th>
                          <th>Work</th>
                          <th>Employee setting</th>
                        </tr>
                      </thead>
                      <tbody>
                        {catalog.flatMap((m) =>
                          m.pages.flatMap((p) =>
                            p.permissions
                              .filter((c) => c.permissionId)
                              .map((c) => (
                                <tr key={c.permissionId}>
                                  <td>
                                    {m.name} / {p.name}
                                  </td>
                                  <td>{c.actionName}</td>
                                  <td>
                                    <select
                                      value={
                                        !overrides.has(c.permissionId!)
                                          ? "inherit"
                                          : overrides.get(c.permissionId!)
                                            ? "allow"
                                            : "deny"
                                      }
                                      onChange={(e) =>
                                        setOverrides((v) => {
                                          const n = new Map(v);
                                          e.target.value === "inherit"
                                            ? n.delete(c.permissionId!)
                                            : n.set(
                                                c.permissionId!,
                                                e.target.value === "allow",
                                              );
                                          return n;
                                        })
                                      }
                                    >
                                      <option value="inherit">
                                        Use role setting
                                      </option>
                                      <option value="allow">Give access</option>
                                      <option value="deny">Block access</option>
                                    </select>
                                  </td>
                                </tr>
                              )),
                          ),
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              <button className="btn btn-primary" onClick={saveEmployee}>
                <AdminActionIcon action="save" />
                Save Access
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
